import React, { useEffect, useRef, useState } from 'react';

interface CountdownTimerProps {
  startDate: Date;
  endDate: Date;
  darkMode: boolean;
  onComplete?: () => void;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ startDate, endDate, darkMode, onComplete }) => {
  const [status, setStatus] = useState<"waiting" | "running" | "finished">("waiting");
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const formatNumber = (num: number) => (num < 10 ? `0${num}` : num);

  const diffTime = (future: Date, base: Date) => {
    const diff = future.getTime() - base.getTime();
    if (diff <= 0) return null;

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / 1000 / 60) % 60),
      seconds: Math.floor((diff / 1000) % 60)
    };
  };

  const startCountdown = () => {
    setStatus("running");
    intervalRef.current = setInterval(() => {
      const now = new Date();
      const remaining = diffTime(endDate, now);

      if (!remaining) {
        setStatus("finished");
        clearInterval(intervalRef.current!);
        if (onComplete) onComplete();
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);
  };
  
  useEffect(() => {
    const now = new Date();
    const waitingTime = diffTime(startDate, now);

    if (waitingTime) {
      setStatus("waiting");
      const timeout = setTimeout(startCountdown, startDate.getTime() - now.getTime());
      return () => clearTimeout(timeout);
    }

    startCountdown();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startDate, endDate]);

  // ✅ Calculamos si falta menos de 12 horas, también en estado "waiting"
  const now = new Date();
  const diffToStart = startDate.getTime() - now.getTime();
  const totalHoursToStart = diffToStart / (1000 * 60 * 60);
  const isLessThanOneDay = totalHoursToStart < 12;

  // ✅ Formatos de fecha y hora en español
  const dateFormatted = startDate.toLocaleDateString("es-ES");
  const timeFormatted = startDate
    .toLocaleTimeString("es-ES", { hour: "numeric", minute: "numeric", hour12: true })
    .replace(".", "")
    .toUpperCase();

  // ✅ Mensaje final
  const startMessage = isLessThanOneDay
    ? `a las ${timeFormatted}`
    : `el ${dateFormatted} a las ${timeFormatted}`;

  return (
    <>
      {status === "waiting" && (
        <div className="timer-waiting">
          <p>La oferta inicia {startMessage}</p>
        </div>
      )}

      {status === "running" && (
        <div className={`${darkMode ? "timer-display-dark" : "timer-display-light"} `}>
          {timeLeft.days > 0 && (
            <div className="timer-unit">
              <span className="timer-value">{formatNumber(timeLeft.days)}</span>
              <span className="timer-label">Días</span>
            </div>
          )}
          <div className="timer-unit">
            <span className="timer-value">{formatNumber(timeLeft.hours)}</span>
            <span className="timer-label">Horas</span>
          </div>
          <div className="timer-unit">
            <span className="timer-value">{formatNumber(timeLeft.minutes)}</span>
            <span className="timer-label">Minutos</span>
          </div>
          <div className="timer-unit">
            <span className="timer-value">{formatNumber(timeLeft.seconds)}</span>
            <span className="timer-label">Segundos</span>
          </div>
        </div>
      )}

      {status === "finished" && (
        <div className="timer-finished">
          <p>La oferta ha finalizado</p>
        </div>
      )}
    </>
  );
};

export default CountdownTimer;
